import * as Sentry from '@sentry/nextjs';
import { AxiosError } from 'axios';

import { getComprehensiveListings } from '@/api/comprehensivelistings';
import { FetchError } from '@/api/error';
import { getAllSfxTracks } from '@/api/sfx/get-all-sfx';
import { getAllTracks } from '@/api/tracks/get-all-tracks';
import { HOUR_INTERVAL } from '@/constants/revalidationInterval';
import { removeKeys } from '@/utils/remove-keys';

import { pageDataApiMap } from './api';

import type { PageData } from './types';
import type { Category } from '@/api/comprehensivelistings';
import type { Redirect } from '@/types/page';
import type { TracksPage } from '@/types/tracks';
import type { GetStaticPropsResult } from 'next';

type AssetType = keyof typeof pageDataApiMap;

export const getPageNumberFromSlug = (
  slugArray: string[] | string | undefined
) => {
  if (!slugArray || typeof slugArray === 'string') {
    return { pageNumber: 1, hasPageNumber: false, slug: [] };
  }

  const lastElement = slugArray[slugArray.length - 1];
  const secondLastElement = slugArray[slugArray.length - 2];

  if (typeof lastElement === 'string' && secondLastElement === 'page') {
    const possiblePageNumber = Number.parseInt(lastElement, 10);

    if (!Number.isNaN(possiblePageNumber)) {
      return {
        pageNumber: possiblePageNumber,
        hasPageNumber: true,
        slug: slugArray.slice(0, -2),
      };
    }
  }

  return { pageNumber: 1, hasPageNumber: false, slug: slugArray };
};

export const getPageData = async <T extends PageData>(
  slugArray: string[],
  {
    assetType,
    pageSlug,
    shouldFetchTracks = true,
    source,
    pageType,
    isr,
    includeComprehensiveListings = false,
  }: {
    assetType: AssetType;
    pageSlug: string[];
    shouldFetchTracks?: boolean;
    source: string;
    pageType?: string;
    isr?: boolean;
    includeComprehensiveListings?: boolean;
  }
): Promise<
  GetStaticPropsResult<{
    pageData: T;
    initialTracks?: TracksPage | null;
  }>
> => {
  try {
    // Strip out the page number from the slug.
    const { slug, pageNumber, hasPageNumber } =
      getPageNumberFromSlug(slugArray);

    // If the page number is 1 and it's included, redirect to the page without the page number.
    // Use 'source' parameter which is already the full cleaned path constructed by all callers.
    if (hasPageNumber && pageNumber === 1) {
      return {
        redirect: {
          destination: source,
          statusCode: 301,
        },
      };
    }

    // Slug ID actually refer to the name e.g. background, sunny, etc.
    const slugId = slug[slug.length - 1];

    const fetchAllTracks =
      assetType === 'music' ? getAllTracks : getAllSfxTracks;

    const isCollection = pageType === 'collection';

    const mapKey = isCollection ? 'collection' : 'category';

    const fetchPageData = pageDataApiMap[assetType][mapKey];

    // Add timeout to avoid long-running requests
    const pageDataPromise = Promise.race([
      fetchPageData(slug, source) as Promise<T | Redirect>,
      new Promise<never>((_, reject) => {
        setTimeout(
          () => reject(new Error('Page data request timeout')),
          15_000
        );
      }),
    ]).catch((error) => {
      Sentry.captureException(error);

      return null;
    });

    const tracksPromise: Promise<void | TracksPage> = shouldFetchTracks
      ? fetchAllTracks({
          id: slugId,
          page: pageNumber,
          isPremium: false,
          type: isCollection ? 'collection' : 'category',
          sort: isCollection ? 'CollectionDefault' : 'Relevance',
        }).catch((error) => {
          Sentry.captureException(error);

          return;
        })
      : Promise.resolve(undefined);

    const comprehensiveListingsPromise = includeComprehensiveListings
      ? getComprehensiveListings(assetType).catch((error) => {
          Sentry.captureException(error);

          return null;
        })
      : Promise.resolve(null);

    const [pageData, initialTracks, comprehensiveListings] = await Promise.all([
      pageDataPromise,
      tracksPromise,
      comprehensiveListingsPromise,
    ]);

    if (typeof pageData !== 'object' || pageData === null) {
      return { notFound: true };
    }

    // Check if the type is a redirect.
    if ('destination' in pageData) {
      let { destination } = pageData;

      if (hasPageNumber) {
        destination = `${destination}/page/${pageNumber}`;
      }

      return {
        redirect: {
          destination,
          permanent: pageData.permanent ?? true,
        },
      };
    }

    const resultsNotFound =
      shouldFetchTracks && initialTracks && !initialTracks?.results?.length;

    if (!pageData || resultsNotFound) {
      return { notFound: true };
    }

    const pageProps: GetStaticPropsResult<{
      pageData: T;
      initialTracks?: TracksPage | null;
      comprehensiveListings?: Category[] | null;
    }> = {
      props: {
        pageData: {
          ...pageData,
          slugId,
          ...(pageType && { pageType }),
        },
        comprehensiveListings,
        // Remove waveform_json from initialTracks to save bandwidth.
        initialTracks:
          shouldFetchTracks && initialTracks
            ? removeKeys(initialTracks, ['waveform_json'])
            : null,
      },
    };

    if (isr) {
      const revalidateInterval = shouldFetchTracks
        ? HOUR_INTERVAL
        : HOUR_INTERVAL * 24 * 3;

      const revalidateOverride = process.env.REVALIDATE_OVERRIDE;

      pageProps.revalidate =
        revalidateOverride === undefined
          ? revalidateInterval
          : Number.parseInt(revalidateOverride, 10);
    }

    return pageProps;
  } catch (error) {
    Sentry.withScope((scope) => {
      scope.setExtra('slug', slugArray);
      scope.setExtra('options', {
        assetType,
        pageSlug,
        shouldFetchTracks,
        source,
        pageType,
        isr,
        includeComprehensiveListings,
      });
      Sentry.captureException(error);
    });

    // Check if the error is an Axios error with a 404 status
    if (error instanceof AxiosError && error.response?.status === 404) {
      return { notFound: true };
    }

    // Check if the error is a FetchError with a 404 status
    if (error instanceof FetchError && error.statusCode === 404) {
      return { notFound: true };
    }

    throw error; // Re-throw other errors to allow handling upstream
  }
};
